/* eslint-disable react/prop-types */
import React, { useMemo } from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import HeroDescription from "@insightfulscience/atomic-react/Hero/Description";
import HeroAction from "@insightfulscience/atomic-react/Hero/Action";
import { HeroCol, HeroRow } from "@insightfulscience/atomic-react/Hero/Grid";
import Button from "@insightfulscience/atomic-react/Button";
import NextLink from "@insightfulscience/smart-react/NextLink";
import HeroButtonLink from "@insightfulscience/atomic-react/Hero/ButtonLink";
import HeroLink from "@insightfulscience/atomic-react/Hero/Link";
import PageNavbarSearch from "@insightfulscience/atomic-react/PageNavbarSearch";
import PageNavbarSearchMain from "@insightfulscience/atomic-react/PageNavbarSearch/Main";
import PageNavbarSearchSidebar from "@insightfulscience/atomic-react/PageNavbarSearch/Sidebar";
import { PlasmidElementSectionBreadcrumbs } from "@insightfulscience/atomic-react/PlasmidElement";
import Breadcrumbs from "@insightfulscience/atomic-react/Breadcrumbs";
import BreadcrumbItem, {
	BreadcrumbItemLink,
} from "@insightfulscience/atomic-react/Breadcrumbs/Item";
import { useLocale } from "@insightfulscience/i18";
import SectionItems from "../ui/SectionItems";
import SectionCopyright from "../ui/SectionCopyright";
import BlockAutocomplete from "../ui/BlockAutocomplete";
import { PageNavbarSearchLink } from "../ui/PageNavbarSearch";
import {
	ROUTE_DOMAIN_PROD,
	ROUTE_FREE_TRIAL,
	ROUTE_HOME,
	ROUTE_PLASMIDS,
	ROUTE_SNAPGENE_VIEWER,
} from "../routes";
import { routeSet } from "../utils/plasmid/helpers";

const SequencesPageDefaultTemplate = ({
	title,
	description,
	setID,
	downloadFile,
	sets,
	sequences,
}) => {
	const [t] = useLocale("plasmids-sequences");
	const url = useMemo(() => `https://${ROUTE_DOMAIN_PROD}${routeSet(setID)}`, []);
	return (
		<>
			<PageMeta title={title} description={description}>
				<PageMeta.OGP
					title={title}
					description={description}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={url}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={url} />
				<PageMeta.Author content={t("META_AUTHOR")} />
				<PageMeta.Twitter
					title={title}
					description={description}
					card={t("META_TWITTER_CARD")}
					creator={t("META_TWITTER_CREATOR")}
					site={t("META_TWITTER_SITE")}
					image={t("META_TWITTER_IMAGE")}
					isImageBaseUrl
				/>
				<PageMeta.Robots content={t("META_ROBOTS")} />
			</PageMeta>
			<Hero>
				<HeroTitle dangerouslySetInnerHTML={{ __html: title }} />
				<HeroDescription dangerouslySetInnerHTML={{ __html: description }} />
				<HeroAction>
					<HeroRow>
						<HeroCol>
							<Button size="s" onClick={() => downloadFile()}>
								{t("DOWNLOAD_PLASMID_SET")}
							</Button>
						</HeroCol>
						<HeroCol>
							<NextLink
								Component={HeroButtonLink}
								href={ROUTE_FREE_TRIAL}
								styleType="secondary"
								isOutline2
							>
								{t("TRY_SNAPGENE_FOR_FREE")}
							</NextLink>
						</HeroCol>
					</HeroRow>
				</HeroAction>
				<HeroRow spacing={{ mt: 2 }}>
					<HeroCol sx={{ textAlign: "center" }}>
						<NextLink Component={HeroLink} href={ROUTE_SNAPGENE_VIEWER}>
							{t("DOWNLOAD_SNAPGENE_VIEWER")}
						</NextLink>
					</HeroCol>
				</HeroRow>
			</Hero>
			<PageNavbarSearch>
				<PageNavbarSearchMain>
					{t("EXPLORE_OVER_PLASMIDS")}:{" "}
					<NextLink Component={PageNavbarSearchLink} href={ROUTE_PLASMIDS}>
						{t("MORE_PLASMID_SETS")}
					</NextLink>
				</PageNavbarSearchMain>
				<PageNavbarSearchSidebar>
					<BlockAutocomplete />
				</PageNavbarSearchSidebar>
			</PageNavbarSearch>
			<PlasmidElementSectionBreadcrumbs>
				<Breadcrumbs>
					<BreadcrumbItem>
						<NextLink Component={BreadcrumbItemLink} href={ROUTE_HOME}>
							{t("HOME")}
						</NextLink>
					</BreadcrumbItem>
					<BreadcrumbItem>
						<NextLink Component={BreadcrumbItemLink} href={ROUTE_PLASMIDS}>
							{t("PLASMIDS")}
						</NextLink>
					</BreadcrumbItem>
					<BreadcrumbItem dangerouslySetInnerHTML={{ __html: title }} />
				</Breadcrumbs>
			</PlasmidElementSectionBreadcrumbs>
			<SectionItems
				type="sequence"
				title={t("INDIVIDUAL_SEQUENCES_MAPS")}
				setID={setID}
				items={sequences}
			/>
			<SectionItems type="set" title={t("PLASMID_SETS")} items={sets} />
			<SectionCopyright />
		</>
	);
};

export default SequencesPageDefaultTemplate;
